import { useEffect, useRef, useState, useCallback } from "react";
import OpenSeadragon from "openseadragon";
import { API } from "@/lib/api";
import { TextNoteDialog } from "@/components/wsi/TextNoteDialog";

// Outils gérant un glisser (drag) pour dessiner
const DRAG_TOOLS = new Set(["rectangle", "ellipse", "fleche", "regle", "libre"]);

export function OSDCanvas({ manifest, wsiId, mobile, activeTool, color,
                            annotations, selectedId, onSelect, onCreate, viewerRef, aiRegions = [] }) {
  const hostRef = useRef(null);
  const osdRef = useRef(null);
  const [, setTick] = useState(0);
  const draftRef = useRef(null);
  const draggingRef = useRef(false);
  const [draft, setDraft] = useState(null);
  const [textAt, setTextAt] = useState(null);

  const rerender = useCallback(() => setTick((t) => (t + 1) % 1000000), []);

  useEffect(() => {
    if (!manifest || !hostRef.current) return;
    const tileSource = {
      width: manifest.width, height: manifest.height,
      tileSize: manifest.tile_size, tileOverlap: manifest.overlap,
      minLevel: 0, maxLevel: manifest.max_level,
      getTileUrl: (level, x, y) =>
        `${API}/wsi/${wsiId}/tiles/${level}/${x}_${y}.jpg${mobile ? "?mobile=1" : ""}`,
    };
    const viewer = OpenSeadragon({
      element: hostRef.current,
      tileSources: tileSource,
      showNavigator: !mobile,
      navigatorPosition: "BOTTOM_RIGHT",
      navigatorHeight: 110, navigatorWidth: 150,
      showNavigationControl: false,
      animationTime: 0.4, springStiffness: 8,
      maxZoomPixelRatio: 2.5, zoomPerScroll: 1.35,
      gestureSettingsMouse: { clickToZoom: false },
      gestureSettingsTouch: { pinchToZoom: true, flickEnabled: true },
      crossOriginPolicy: false, visibilityRatio: 0.6,
    });
    osdRef.current = viewer;
    if (viewerRef) viewerRef.current = viewer;
    ["update-viewport", "animation", "resize", "open"].forEach((ev) =>
      viewer.addHandler(ev, rerender));
    return () => { viewer.destroy(); osdRef.current = null; if (viewerRef) viewerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest, wsiId, mobile]);

  // ---------- conversions ----------
  const toPx = (ix, iy) => {
    const v = osdRef.current;
    if (!v) return [0, 0];
    const vp = v.viewport.imageToViewportCoordinates(new OpenSeadragon.Point(ix, iy));
    const p = v.viewport.pixelFromPoint(vp, true);
    return [p.x, p.y];
  };
  const toImg = (px, py) => {
    const v = osdRef.current;
    const vp = v.viewport.pointFromPixel(new OpenSeadragon.Point(px, py), true);
    const img = v.viewport.viewportToImageCoordinates(vp);
    return [img.x, img.y];
  };
  const evtImg = (e) => {
    const r = hostRef.current.getBoundingClientRect();
    return toImg(e.clientX - r.left, e.clientY - r.top);
  };

  // ---------- construction de géométrie ----------
  const finalize = (d) => {
    if (!d) return;
    let geometrie = null, mesure = null;
    if (d.type === "rectangle" || d.type === "ellipse") {
      const [x0, y0] = d.start, [x1, y1] = d.cur;
      geometrie = { x: Math.min(x0, x1), y: Math.min(y0, y1),
                    w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) };
      if (geometrie.w < 3 && geometrie.h < 3) return;
    } else if (d.type === "fleche" || d.type === "regle") {
      geometrie = { x1: d.start[0], y1: d.start[1], x2: d.cur[0], y2: d.cur[1] };
      if (d.type === "regle") {
        const px = Math.hypot(d.cur[0] - d.start[0], d.cur[1] - d.start[1]);
        mesure = manifest.mpp ? +(px * manifest.mpp).toFixed(1) : +px.toFixed(0);
      }
    } else if (d.type === "libre" || d.type === "polygone") {
      if (d.points.length < 2) return;
      geometrie = { points: d.points };
    } else if (d.type === "point") {
      geometrie = { x: d.start[0], y: d.start[1] };
    } else if (d.type === "texte") {
      geometrie = { x: d.start[0], y: d.start[1] };
    }
    onCreate({ type: d.type, geometrie, couleur: color, texte: d.texte || null, mesure });
  };

  // ---------- interactions de dessin ----------
  const onPointerDown = (e) => {
    if (!activeTool || !osdRef.current) return;
    e.preventDefault();
    const [ix, iy] = evtImg(e);
    if (activeTool === "point") { finalize({ type: "point", start: [ix, iy] }); return; }
    if (activeTool === "texte") {
      setTextAt([ix, iy]);
      return;
    }
    if (activeTool === "polygone") {
      const d = draftRef.current && draftRef.current.type === "polygone"
        ? { ...draftRef.current, points: [...draftRef.current.points, [ix, iy]] }
        : { type: "polygone", points: [[ix, iy]] };
      draftRef.current = d; setDraft(d); return;
    }
    if (DRAG_TOOLS.has(activeTool)) {
      draggingRef.current = true;
      const d = activeTool === "libre"
        ? { type: "libre", points: [[ix, iy]] }
        : { type: activeTool, start: [ix, iy], cur: [ix, iy] };
      draftRef.current = d; setDraft(d);
    }
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current || !draftRef.current) return;
    const [ix, iy] = evtImg(e);
    const d = draftRef.current;
    const nd = d.type === "libre" ? { ...d, points: [...d.points, [ix, iy]] } : { ...d, cur: [ix, iy] };
    draftRef.current = nd; setDraft(nd);
  };
  const onPointerUp = () => {
    if (draggingRef.current && draftRef.current) {
      finalize(draftRef.current);
      draggingRef.current = false; draftRef.current = null; setDraft(null);
    }
  };
  const onDoubleClick = (e) => {
    if (activeTool === "polygone" && draftRef.current) {
      e.preventDefault();
      finalize(draftRef.current); draftRef.current = null; setDraft(null);
    }
  };

  // Annuler le dessin en cours via évènement externe
  useEffect(() => {
    if (activeTool === null && draftRef.current) { draftRef.current = null; setDraft(null); }
  }, [activeTool]);

  // ---------- rendu SVG ----------
  const stroke = (a) => a.couleur || "#F59E0B";
  const ready = osdRef.current && osdRef.current.world && osdRef.current.world.getItemCount() > 0;
  const renderShape = (a, key, isSel) => {
    if (!ready) return null;  // évite les coords NaN avant le chargement de l'image
    const g = a.geometrie || {};
    const sw = isSel ? 4 : 2.5;
    const col = stroke(a);
    const common = { stroke: col, strokeWidth: sw, fill: "none",
      onClick: (e) => { e.stopPropagation(); onSelect && onSelect(a.id); },
      style: { cursor: "pointer" }, "data-testid": `wsi-annotation-${a.id || "draft"}` };
    if (a.type === "rectangle") {
      const [x, y] = toPx(g.x, g.y), [x2, y2] = toPx(g.x + g.w, g.y + g.h);
      return <rect key={key} x={x} y={y} width={x2 - x} height={y2 - y} {...common} />;
    }
    if (a.type === "ellipse") {
      const [x, y] = toPx(g.x, g.y), [x2, y2] = toPx(g.x + g.w, g.y + g.h);
      return <ellipse key={key} cx={(x + x2) / 2} cy={(y + y2) / 2}
                      rx={Math.abs(x2 - x) / 2} ry={Math.abs(y2 - y) / 2} {...common} />;
    }
    if (a.type === "fleche" || a.type === "regle") {
      const [x1, y1] = toPx(g.x1, g.y1), [x2, y2] = toPx(g.x2, g.y2);
      return (
        <g key={key}>
          <line x1={x1} y1={y1} x2={x2} y2={y2} {...common}
                markerEnd={a.type === "fleche" ? "url(#arrow)" : undefined} />
          {a.type === "regle" && a.mesure != null && (
            <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 6} fill={col} fontSize="13"
                  fontWeight="600" textAnchor="middle">
              {a.mesure}{manifest.mpp ? " µm" : " px"}
            </text>
          )}
        </g>
      );
    }
    if (a.type === "libre" || a.type === "polygone") {
      const pts = (g.points || []).map(([ix, iy]) => toPx(ix, iy).join(",")).join(" ");
      const El = a.type === "polygone" ? "polygon" : "polyline";
      return <El key={key} points={pts} {...common} />;
    }
    if (a.type === "point") {
      const [x, y] = toPx(g.x, g.y);
      return <circle key={key} cx={x} cy={y} r={6} fill={col} stroke="#fff" strokeWidth="2"
                     onClick={(e) => { e.stopPropagation(); onSelect && onSelect(a.id); }}
                     style={{ cursor: "pointer" }} />;
    }
    if (a.type === "texte") {
      const [x, y] = toPx(g.x, g.y);
      return (
        <g key={key} onClick={(e) => { e.stopPropagation(); onSelect && onSelect(a.id); }}
           style={{ cursor: "pointer" }}>
          <circle cx={x} cy={y} r={4} fill={col} />
          <text x={x + 8} y={y + 4} fill={col} fontSize="14" fontWeight="600">{a.texte}</text>
        </g>
      );
    }
    return null;
  };

  const aiColor = (s) => (s >= 0.85 ? "#DC2626" : s >= 0.7 ? "#F97316" : "#F59E0B");
  const renderAIRegion = (r, i) => {
    if (!ready) return null;
    const c = (r.geometrie || {}).coords || [];
    if (c.length < 2) return null;
    const [x, y] = toPx(c[0][0], c[0][1]);
    const [x2, y2] = toPx(c[1][0], c[1][1]);
    const col = aiColor(r.score || 0);
    return (
      <g key={`ai-${r.id || i}`} data-testid={`wsi-ai-roi-${r.id || i}`}>
        <rect x={Math.min(x, x2)} y={Math.min(y, y2)} width={Math.abs(x2 - x)} height={Math.abs(y2 - y)}
              stroke={col} strokeWidth={3} strokeDasharray="8 4" fill={col} fillOpacity="0.12" />
        <text x={Math.min(x, x2)} y={Math.min(y, y2) - 5} fill={col} fontSize="13" fontWeight="700">
          IA {Math.round((r.score || 0) * 100)}%
        </text>
      </g>
    );
  };

  return (
    <div className="relative w-full h-full" data-testid="wsi-osd-wrapper">
      <div ref={hostRef} className="w-full h-full" style={{ background: "#0b1220" }}
           data-testid="wsi-osd-host" />
      <svg className="absolute inset-0 w-full h-full"
           style={{ pointerEvents: activeTool ? "auto" : "none",
                    cursor: activeTool ? "crosshair" : "default" }}
           onPointerDown={onPointerDown} onPointerMove={onPointerMove}
           onPointerUp={onPointerUp} onDoubleClick={onDoubleClick}
           data-testid="wsi-annotation-layer">
        <defs>
          <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3"
                  orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L8,3 L0,6 Z" fill="context-stroke" />
          </marker>
        </defs>
        {annotations.map((a) => renderShape(a, a.id, a.id === selectedId))}
        {aiRegions.map((r, i) => renderAIRegion(r, i))}
        {draft && renderShape(draft, "draft", false)}
      </svg>
      {textAt && <TextNoteDialog onCancel={() => setTextAt(null)}
                                 onSave={(txt) => { finalize({ type: "texte", start: textAt, texte: txt }); setTextAt(null); }} />}
    </div>
  );
}
